import type { NearestPropertyRow, DistanceResult } from './types';
import { classifyMgmtType, MGMT_TYPE_FIELD, type MgmtTypeClassification } from '../utils/mgmtType';

/**
 * Convert raw worker distance results into typed NearestPropertyRow[],
 * sorted by distance ascending.
 *
 * @param results - Raw distance results from the worker
 * @param excludePropId - PropID to exclude from results (the source property).
 *   Always matches against the `PropID` attribute since results come from the boundaries layer.
 */
export function mapDistanceResults(
  results: DistanceResult[],
  excludePropId?: number,
): NearestPropertyRow[] {
  return results
    .filter((r) => {
      if (!r?.property?.attributes) return false;
      // Exclude the source property itself from results
      if (excludePropId != null) {
        const attrs = r.property.attributes as Record<string, unknown>;
        const featurePropId = attrs.PropID;
        if (Number(featurePropId) === excludePropId) return false;
      }
      return true;
    })
    .map((r) => {
      const attrs = r.property.attributes as Record<string, unknown>;
      return {
        propId: (attrs.PropID ?? attrs.OBJECTID) as number,
        objectId: attrs.OBJECTID as number,
        name: (attrs.NAME as string) ?? 'Unknown',
        division: attrs.Division as string | undefined,
        managementType: classifyMgmtType(attrs[MGMT_TYPE_FIELD] as string | null | undefined),
        acres: (attrs.GIS_ACRES ?? attrs.ACRES ?? attrs.GIS_Acres) as number | undefined,
        transactionId: attrs.TransactionID as number | undefined,
        distanceRaw: r.distance,
      };
    })
    .sort((a, b) => a.distanceRaw - b.distanceRaw);
}

/**
 * Filter rows to only include properties matching the given boundary-map
 * `MgmtType` classifications (`DirectOp` | `Lease` | `Contract` | `Unknown`).
 */
export function filterByManagementTypes(
  rows: NearestPropertyRow[],
  managementTypes: MgmtTypeClassification[],
): NearestPropertyRow[] {
  if (!managementTypes.length) return rows;
  const typeSet = new Set(managementTypes);
  return rows.filter((r) => typeSet.has(r.managementType));
}

/**
 * Apply top-N-per-type bucketing: return at most `topN` rows per unique
 * boundary-map `MgmtType` classification, preserving distance-ascending sort
 * within each bucket.
 */
export function applyTopNPerType(
  rows: NearestPropertyRow[],
  topN: number,
): NearestPropertyRow[] {
  const buckets: Record<string, NearestPropertyRow[]> = {};
  const result: NearestPropertyRow[] = [];

  for (const row of rows) {
    const key = row.managementType;
    if (!buckets[key]) buckets[key] = [];
    if (buckets[key]!.length < topN) {
      buckets[key]!.push(row);
      result.push(row);
    }
  }

  return result.sort((a, b) => a.distanceRaw - b.distanceRaw);
}

/**
 * Apply a hard limit cap to the rows.
 */
export function applyLimit(
  rows: NearestPropertyRow[],
  limit: number,
): NearestPropertyRow[] {
  return rows.slice(0, limit);
}

/**
 * Group rows by boundary-map `MgmtType` classification.
 */
export function groupByManagementType(
  rows: NearestPropertyRow[],
): Record<string, NearestPropertyRow[]> {
  const groups: Record<string, NearestPropertyRow[]> = {};
  for (const row of rows) {
    const key = row.managementType;
    if (!groups[key]) groups[key] = [];
    groups[key]!.push(row);
  }
  return groups;
}

/**
 * Apply client-side text filter on rows (post-worker, main thread).
 * Matches case-insensitively against name, division, and managementType.
 */
export function applyTableFilter(
  rows: NearestPropertyRow[],
  filter: string,
): NearestPropertyRow[] {
  if (!filter.trim()) return rows;
  const q = filter.toLowerCase();
  return rows.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      (r.division?.toLowerCase().includes(q) ?? false) ||
      r.managementType.toLowerCase().includes(q),
  );
}

/**
 * Full post-processing pipeline: exclude source → management type filter → topNPerType → limit.
 */
export function postProcessRows(
  rawResults: DistanceResult[],
  options: {
    excludePropId?: number;
    managementTypes?: MgmtTypeClassification[];
    topNPerType?: number;
    limit?: number;
  },
): NearestPropertyRow[] {
  let rows = mapDistanceResults(rawResults, options.excludePropId);

  if (options.managementTypes?.length) {
    rows = filterByManagementTypes(rows, options.managementTypes);
  }

  if (options.topNPerType != null) {
    rows = applyTopNPerType(rows, options.topNPerType);
  }

  if (options.limit != null) {
    rows = applyLimit(rows, options.limit);
  }

  return rows;
}

